import { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { Floating } from './Floating';

// ─────────────────────────────────────────────────────────────────────────────
// DateTimeInput — the ONE date / date-time picker for the whole app.
//
// Replaces native <input type="date"> and <input type="datetime-local">, which
// look different on every browser/OS and show the browser's locale format.
//
// Values keep the native formats so call sites are drop-in:
//   mode="date"     → "yyyy-MM-dd"
//   mode="datetime" → "yyyy-MM-ddTHH:mm" (local time, 24h)
// The field always shows "dd-MM-yyyy" / "dd-MM-yyyy hh:mm AM".
// ─────────────────────────────────────────────────────────────────────────────

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const pad = (n: number) => String(n).padStart(2, '0');
const toDateStr = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toTimeStr = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

interface Parsed { date: string; time: string }

function parse(value: string | null | undefined): Parsed | null {
  if (!value) return null;
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
  if (!m) return null;
  return { date: `${m[1]}-${m[2]}-${m[3]}`, time: m[4] ? `${m[4]}:${m[5]}` : '' };
}

/** "yyyy-MM-dd" → "dd-MM-yyyy" */
export function formatInputDate(date: string): string {
  const [y, m, d] = date.split('-');
  return y && m && d ? `${d}-${m}-${y}` : date;
}

/** "HH:mm" → "hh:mm AM" */
export function formatInputTime(time: string): string {
  const [h, m] = time.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return time;
  return `${pad(h % 12 || 12)}:${pad(m)} ${h >= 12 ? 'PM' : 'AM'}`;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  mode?: 'date' | 'datetime';
  /** Same format as value. */
  min?: string;
  max?: string;
  placeholder?: string;
  className?: string;
  /** Extra classes for the visible field (height, padding, text size). */
  inputClassName?: string;
  disabled?: boolean;
  clearable?: boolean;
  id?: string;
}

export function DateTimeInput({
  value,
  onChange,
  mode = 'date',
  min,
  max,
  placeholder,
  className = '',
  inputClassName = '',
  disabled,
  clearable = true,
  id,
}: Props) {
  const withTime = mode === 'datetime';
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const parsed = parse(value);
  const minDate = parse(min)?.date;
  const maxDate = parse(max)?.date;

  // Month on screen: the selected date's month, else today's.
  const [view, setView] = useState(() => {
    const base = parsed ? new Date(`${parsed.date}T00:00:00`) : new Date();
    return { y: base.getFullYear(), m: base.getMonth() };
  });
  useEffect(() => {
    if (!open) return;
    const base = parsed ? new Date(`${parsed.date}T00:00:00`) : new Date();
    setView({ y: base.getFullYear(), m: base.getMonth() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const todayStr = toDateStr(new Date());

  const cells = useMemo(() => {
    const first = new Date(view.y, view.m, 1);
    const start = new Date(view.y, view.m, 1 - first.getDay());
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
      return { str: toDateStr(d), day: d.getDate(), inMonth: d.getMonth() === view.m };
    });
  }, [view]);

  const years = useMemo(() => {
    const now = new Date().getFullYear();
    let from = Math.min(now - 100, view.y);
    let to = Math.max(now + 10, view.y);
    if (minDate) from = Math.max(from, Number(minDate.slice(0, 4)));
    if (maxDate) to = Math.min(to, Number(maxDate.slice(0, 4)));
    from = Math.min(from, view.y);
    to = Math.max(to, view.y);
    return Array.from({ length: to - from + 1 }, (_, i) => from + i);
  }, [view.y, minDate, maxDate]);

  const isDisabled = (d: string) => (!!minDate && d < minDate) || (!!maxDate && d > maxDate);

  const emit = (date: string, time: string) => onChange(withTime ? `${date}T${time}` : date);
  const currentTime = () => parsed?.time || toTimeStr(new Date());

  const pickDay = (d: string) => {
    if (isDisabled(d)) return;
    emit(d, currentTime());
    if (!withTime) setOpen(false);
  };

  const shiftMonth = (delta: number) => setView(v => {
    const d = new Date(v.y, v.m + delta, 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  // Time row (datetime only): 12h selects that write back 24h.
  // With no time picked yet, the selects start at the current time.
  const [hh, mm] = (parsed?.time || toTimeStr(new Date())).split(':').map(Number);
  const hour12 = hh % 12 || 12;
  const ampm: 'AM' | 'PM' = hh >= 12 ? 'PM' : 'AM';
  const setTime = (h12: number, minute: number, ap: 'AM' | 'PM') => {
    const h24 = (h12 % 12) + (ap === 'PM' ? 12 : 0);
    emit(parsed?.date || todayStr, `${pad(h24)}:${pad(minute)}`);
  };

  const setToday = () => {
    const now = new Date();
    emit(toDateStr(now), toTimeStr(now));
    setView({ y: now.getFullYear(), m: now.getMonth() });
    if (!withTime) setOpen(false);
  };

  const shown = parsed
    ? withTime && parsed.time
      ? `${formatInputDate(parsed.date)} ${formatInputTime(parsed.time)}`
      : formatInputDate(parsed.date)
    : '';

  const selectCls = 'h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#6366F1]/30';

  return (
    <div ref={anchorRef} className={`relative ${className}`}>
      <button
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className={`flex w-full items-center justify-between gap-2 h-10 rounded-lg border border-slate-200 bg-white px-3 text-left text-sm transition-colors ${disabled ? 'cursor-not-allowed opacity-50' : 'hover:border-slate-300'} ${open ? 'border-[#6366F1] ring-2 ring-[#6366F1]/20' : ''} ${inputClassName}`}
      >
        <span className={`truncate ${shown ? 'text-slate-800' : 'text-slate-400'}`}>
          {shown || placeholder || (withTime ? 'dd-MM-yyyy hh:mm' : 'dd-MM-yyyy')}
        </span>
        <Calendar className="h-4 w-4 flex-shrink-0 text-slate-400" />
      </button>

      <Floating
        open={open}
        anchorRef={anchorRef}
        onClose={() => setOpen(false)}
        matchWidth={false}
        gap={6}
        className="w-[300px] rounded-2xl border border-slate-200 bg-white p-4 shadow-xl"
      >
        <div role="dialog" aria-label={withTime ? 'Choose date and time' : 'Choose date'}>
          {/* Month / year navigation */}
          <div className="mb-3 flex items-center justify-between gap-1">
            <button type="button" onClick={() => shiftMonth(-1)} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100" aria-label="Previous month">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-1">
              <select value={view.m} onChange={e => setView(v => ({ ...v, m: Number(e.target.value) }))} className="rounded-md bg-transparent px-1 py-1 text-sm font-medium text-slate-800 hover:bg-slate-50 focus:outline-none">
                {MONTHS.map((name, i) => <option key={name} value={i}>{name}</option>)}
              </select>
              <select value={view.y} onChange={e => setView(v => ({ ...v, y: Number(e.target.value) }))} className="rounded-md bg-transparent px-1 py-1 text-sm font-medium text-slate-800 hover:bg-slate-50 focus:outline-none">
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button type="button" onClick={() => shiftMonth(1)} className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100" aria-label="Next month">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-1 text-center">
            {WEEKDAYS.map(w => <div key={w} className="py-1 text-xs font-medium text-slate-500">{w}</div>)}
            {cells.map(c => {
              const selected = parsed?.date === c.str;
              const today = c.str === todayStr;
              const off = isDisabled(c.str);
              return (
                <button
                  key={c.str}
                  type="button"
                  disabled={off}
                  onClick={() => pickDay(c.str)}
                  className={`h-9 rounded-lg text-sm transition-colors ${
                    selected
                      ? 'bg-[#6366F1] font-semibold text-white'
                      : today
                        ? 'border border-[#6366F1]/40 font-semibold text-slate-900 hover:bg-indigo-50'
                        : c.inMonth
                          ? 'text-slate-800 hover:bg-slate-100'
                          : 'text-slate-300 hover:bg-slate-50'
                  } ${off ? 'cursor-not-allowed opacity-30 hover:bg-transparent' : ''}`}
                >
                  {c.day}
                </button>
              );
            })}
          </div>

          {withTime && (
            <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
              <span className="mr-1 text-sm text-slate-500">Time</span>
              <select value={hour12} onChange={e => setTime(Number(e.target.value), mm, ampm)} className={selectCls} aria-label="Hour">
                {Array.from({ length: 12 }, (_, i) => i + 1).map(h => <option key={h} value={h}>{pad(h)}</option>)}
              </select>
              <span className="text-slate-400">:</span>
              <select value={mm} onChange={e => setTime(hour12, Number(e.target.value), ampm)} className={selectCls} aria-label="Minute">
                {Array.from({ length: 60 }, (_, i) => i).map(m => <option key={m} value={m}>{pad(m)}</option>)}
              </select>
              <select value={ampm} onChange={e => setTime(hour12, mm, e.target.value as 'AM' | 'PM')} className={selectCls} aria-label="AM or PM">
                <option value="AM">AM</option>
                <option value="PM">PM</option>
              </select>
            </div>
          )}

          <div className="mt-3 flex items-center justify-between">
            {clearable ? (
              <button type="button" onClick={() => { onChange(''); setOpen(false); }} className="px-1 text-sm text-slate-500 hover:text-slate-700">
                Clear
              </button>
            ) : <span />}
            <button type="button" onClick={setToday} className="px-1 text-sm font-medium text-[#6366F1] hover:text-[#4F46E5]">
              Today
            </button>
            {withTime ? (
              <button type="button" onClick={() => setOpen(false)} className="rounded-lg bg-[#6366F1] px-4 py-1.5 text-sm font-medium text-white hover:bg-[#4F46E5]">
                Done
              </button>
            ) : <span />}
          </div>
        </div>
      </Floating>
    </div>
  );
}
